import { useQueryClient } from '@tanstack/react-query';
import { useCallback } from 'react';
import type { DatovkyApiSeznam } from '@gov-nx/api';
import { fetchDataBoxesQuery, useAxiosInstance, usePoQuery } from '@gov-nx/api';
import { useAppContext, useExtraConfigContext } from '@gov-nx/core/app';
import { pipe } from '@gov-nx/core/types';
import { useDataBoxStore } from '@gov-nx/store';
import {
	sortDataboxesBeforeStoringToState,
	transformDataBoxes,
} from '@gov-nx/utils/common';

const queryKey = ['data-box-list'];

export const useDataBoxLoader = () => {
	const { setAppError } = useAppContext();
	const { extraConfig } = useExtraConfigContext();
	const setDataBoxList = useDataBoxStore('setDataBoxList');
	const databoxTransformConfig =
		extraConfig.pageRoutes.databoxConfig.databoxTransformConfig;

	const onSuccess = (data: DatovkyApiSeznam) => {
		if (!data || !data.seznam || !Array.isArray(data.seznam)) {
			setAppError(new Error('DataBoxLoader: Invalid data received from API'));
			return;
		}

		setDataBoxList(
			data.seznam.length
				? pipe(
						data.seznam,
						transformDataBoxes(databoxTransformConfig),
						sortDataboxesBeforeStoringToState
					)
				: []
		);
	};

	const queryClient = useQueryClient();
	const { axiosInstance } = useAxiosInstance();
	const query = usePoQuery({
		queryKey,
		queryFn: () => fetchDataBoxesQuery(axiosInstance),
		onSuccess,
		onError: setAppError,
		enabled: false,
	});

	const initDataBoxes = useCallback(async (): Promise<void> => {
		await query.refetch();
	}, [query]);

	const refetchDataBoxes = useCallback(async (): Promise<void> => {
		await queryClient.invalidateQueries({ queryKey });

		await query.refetch();
	}, [query, queryClient]);

	return {
		isFetched: query.isFetched,
		initDataBoxes,
		refetchDataBoxes,
		data: query.data,
	};
};
